import { Dispatch, FC, MouseEvent, useCallback } from 'react';
import { MenuItem } from '@mui/material';
import { EmptyFunction } from '~/adapters/typescript/emptyFunction';
import { Design } from '~/adapters/services/accounts/designs/types';
import { useDesignQuote, useProductDetails } from '~/adapters/services/accounts/designs/hooks';
import { useAddToCartOrRedirect } from '~/adapters/services/rfe/hooks';
import { trackEvent } from '~/adapters/tracking/tracking';
import { AnalyticsConstants } from '~/adapters/tracking/AnalyticsConstants';
import setLocation from '~/adapters/browser/setLocation';
import { checkout, internalBuyLink, reorderLink } from '~/adapters/services/rfe/links';
import { getImpersonateQueryVariable } from '~/adapters/browser/getImpersonateQueryVariable';
import { gaCategoryOld, gaCreateDimension, trackGA4AddToCart } from '../tracking';

export type AddToCartMenuItemProps = {
    handleClose: EmptyFunction;
    design: Design;
    cardIndex: number;
    buyWaiting: boolean;
    setBuyWaiting: Dispatch<boolean>;
};

type ExternalAddToCartMenuItemProps = AddToCartMenuItemProps;

const ExternalAddToCartMenuItem: FC<ExternalAddToCartMenuItemProps> = ({
    handleClose,
    design,
    cardIndex,
    setBuyWaiting,
    buyWaiting,
}) => {
    const shouldReorder = Boolean(design.mostRecentOrderId);
    const { data: quoteData, isLoading: isProductDetailsLoading } = useDesignQuote(design.compositeId);
    const { data: productDetailsData } = useProductDetails(design.compositeId, !isProductDetailsLoading);
    const addToCartOrRedirect = useAddToCartOrRedirect();
    const buttonLabel = shouldReorder ? 'Reorder' : 'Add to Cart';
    const navigateToBuy = useCallback(async () => {
        trackEvent({
            category: gaCategoryOld,
            action: 'Button Click',
            label: buttonLabel,
            dimension: {
                ...gaCreateDimension(cardIndex),
                [AnalyticsConstants.isDesignsOrdered]: design.isLocked,
            },
        });
        trackGA4AddToCart(design, cardIndex, quoteData, productDetailsData);
        handleClose();

        // following lines will cause redirect
        setBuyWaiting(true);
        if (shouldReorder) {
            setLocation(reorderLink(design.mostRecentOrderId as number));
            return;
        }

        await addToCartOrRedirect([design.compositeId]);
        setBuyWaiting(false);
        setLocation(checkout());
    }, [quoteData, addToCartOrRedirect, productDetailsData, design, cardIndex, buttonLabel]);

    return (
        <MenuItem onClick={navigateToBuy} disabled={buyWaiting}>
            {buttonLabel}
        </MenuItem>
    );
};

type InternalAddToCartMenuItemProps = Pick<AddToCartMenuItemProps, 'design' | 'handleClose'>;

const InternalAddToCartMenuItem: FC<InternalAddToCartMenuItemProps> = ({ design, handleClose }) => {
    const buttonLabel = design.mostRecentOrderId ? 'Internal Reorder' : 'Internal Checkout';

    const navigateToInternalCheckoutOrReorder = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            handleClose();
            e.preventDefault();
            setLocation(internalBuyLink(design.designId, design.compositeId, design.mostRecentOrderId));
        },
        [handleClose, setLocation, design],
    );

    return <MenuItem onClick={navigateToInternalCheckoutOrReorder}>{buttonLabel}</MenuItem>;
};

// boilerplate from BuyButton.tsx, ExternalBuyButton.tsx and InternalBuyButton.tsx
// it's not worth to abstract common logic because it's a test; only one alternative will stay eventually
export const AddToCartMenuItem: FC<AddToCartMenuItemProps> = (props) => {
    const isImpersonate = Boolean(getImpersonateQueryVariable());
    // eslint-disable-next-line react/jsx-props-no-spreading
    return isImpersonate ? <InternalAddToCartMenuItem {...props} /> : <ExternalAddToCartMenuItem {...props} />;
};
